import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Loader2, AlertTriangle, Lock, Mail, KeyRound } from 'lucide-react';
import { ADMIN_API, setAdminToken, apiError, C } from './adminApi';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${ADMIN_API}/login`, {
        email, password, totp: totp || null, remember_device: remember,
      });
      setAdminToken(data.token);
      navigate('/admin');
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 60%, #14314f 100%)` }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3" style={{ background: C.gold }}>
            <ShieldCheck className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">EasyPassing Admin</h1>
          <p className="text-white/60 text-sm mt-1">Secure administrator access</p>
        </div>

        <form onSubmit={submit} data-testid="admin-login-form" className="bg-white rounded-3xl shadow-2xl p-7 space-y-4">
          {error && (
            <div data-testid="admin-login-error" className="flex items-start gap-2 text-sm rounded-xl bg-red-50 border border-red-200 text-red-800 px-3 py-2.5">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}
          <Field icon={Mail} label="Email">
            <input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full outline-none text-[15px]" placeholder="owner@easypassing.life" />
          </Field>
          <Field icon={Lock} label="Password">
            <input data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full outline-none text-[15px]" placeholder="••••••••" />
          </Field>
          <Field icon={KeyRound} label="Two-factor code">
            <input data-testid="admin-totp" inputMode="numeric" value={totp} onChange={(e) => setTotp(e.target.value)}
              className="w-full outline-none text-[15px] tracking-widest" placeholder="6-digit code" />
          </Field>
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 text-[#57534E] cursor-pointer">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="admin-remember" />
              Remember this device
            </label>
            <button type="button" onClick={() => setError('Contact the account owner to reset your admin password.')}
              className="font-medium" style={{ color: C.navy2 }}>Forgot password?</button>
          </div>
          <button data-testid="admin-login-submit" type="submit" disabled={loading}
            className="w-full h-12 rounded-full text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: C.navy }}>
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Sign in securely'}
          </button>
        </form>
        <p className="text-center text-white/40 text-xs mt-4">Protected area · all access is logged</p>
      </div>
    </div>
  );
}

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-[#57534E] mb-1.5">{label}</label>
    <div className="flex items-center gap-2.5 border border-[#E3E1DC] rounded-xl px-3.5 h-12 focus-within:border-[#2B4C7E]">
      <Icon className="h-4 w-4 text-[#9C948B] shrink-0" />
      {children}
    </div>
  </div>
);
